
import React, { useState } from 'react';
import { User } from '../types';
import { api } from '../services/apiService';
import { MOCK_USERS } from '../constants';

interface LoginProps {
  onLogin: (user: User) => Promise<void> | void;
}

const API_BASE = (import.meta.env.VITE_API_URL as string) || '/api/index.php';

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset password flow
  const urlToken = new URLSearchParams(window.location.search).get('reset_token') || '';
  const [resetMode, setResetMode] = useState<'login' | 'forgot' | 'code'>(urlToken ? 'code' : 'login');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState(urlToken);
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const user = await api.login(email, password);

      if (user) {
        await onLogin(user);
      } else {
        setError("Identifiants incorrects.");
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      setError("Erreur technique. Réessayez.");
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;
    setResetLoading(true);
    setResetMsg('');
    try {
      const res = await fetch(`${API_BASE}?action=forgot_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });
      const data = await res.json();
      if (data.success) {
        setResetMsg('Si ce compte existe, un lien de réinitialisation a été envoyé.');
        setResetMode('code');
      } else {
        setResetMsg(data.error || 'Erreur');
      }
    } catch {
      setResetMsg('Erreur technique.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetCode.trim() || !resetNewPassword.trim()) return;
    setResetLoading(true);
    setResetMsg('');
    try {
      const res = await fetch(`${API_BASE}?action=reset_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetCode, password: resetNewPassword })
      });
      const data = await res.json();
      if (data.success) {
        setResetMsg('Mot de passe modifié ! Vous pouvez vous connecter.');
        setTimeout(() => {
          setResetMode('login');
          setResetMsg('');
          setResetCode('');
          setResetNewPassword('');
        }, 2000);
      } else {
        setResetMsg(data.error || 'Code invalide ou expiré.');
      }
    } catch {
      setResetMsg('Erreur technique.');
    } finally {
      setResetLoading(false);
    }
  };

  const quickFill = (u: typeof MOCK_USERS[0]) => {
      setEmail(u.email);
      setPassword(u.password || '123');
  };

  return (
    <div className="orion-login-page min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      {/* Animated background orbs */}
      <div className="orion-login-orb orion-login-orb-1" />
      <div className="orion-login-orb orion-login-orb-2" />
      <div className="orion-login-orb orion-login-orb-3" />

      <div className="orion-login-card w-full max-w-[420px] relative z-10">
        {/* Logo section */}
        <div className="flex flex-col items-center pt-10 pb-6 px-8">
          <img
            src="/images/logo-orion.png"
            alt="ORION CRM"
            className="h-14 w-auto object-contain mb-6 drop-shadow-[0_0_24px_rgba(17,197,214,0.25)]"
          />
          <p className="text-sm font-medium text-[#7f93bc] tracking-wide">
            {resetMode === 'login' ? 'Connectez-vous pour continuer' :
             resetMode === 'forgot' ? 'Réinitialiser votre mot de passe' :
             'Entrez le code reçu'}
          </p>
        </div>

        {/* Separator */}
        <div className="mx-8 h-px bg-gradient-to-r from-transparent via-[rgba(100,140,210,0.3)] to-transparent" />

        {/* Form section */}
        <div className="p-8">
          {/* ========== LOGIN FORM ========== */}
          {resetMode === 'login' && (
            <>
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="flex items-center gap-2.5 p-3.5 rounded-xl border border-rose-500/30 bg-rose-500/10 animate-in fade-in">
                    <svg className="w-4 h-4 text-rose-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span className="text-xs font-bold text-rose-300">{error}</span>
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-[#7f93bc] uppercase tracking-[0.15em] ml-1">Email professionnel</label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#546a96]">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="orion-login-input w-full pl-10 pr-4 py-3.5 rounded-xl text-sm font-semibold"
                      placeholder="admin@orion.com"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-[#7f93bc] uppercase tracking-[0.15em] ml-1">Mot de passe</label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#546a96]">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="orion-login-input w-full pl-10 pr-4 py-3.5 rounded-xl text-sm font-semibold"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="orion-login-submit w-full py-4 rounded-xl text-xs font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2.5 mt-2 disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                      Connexion...
                    </>
                  ) : (
                    <>
                      Se connecter
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => { setResetMode('forgot'); setResetEmail(email); setResetMsg(''); }}
                  className="text-[11px] font-bold text-[#546a96] hover:text-[#11c5d6] transition-colors"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            </>
          )}

          {/* ========== FORGOT PASSWORD FORM ========== */}
          {resetMode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              {resetMsg && (
                <div className="p-3.5 rounded-xl border border-[rgba(17,197,214,0.3)] bg-[rgba(17,197,214,0.1)] text-xs font-bold text-[#11c5d6]">
                  {resetMsg}
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-[#7f93bc] uppercase tracking-[0.15em] ml-1">Votre email</label>
                <input
                  type="email"
                  required
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="orion-login-input w-full px-4 py-3.5 rounded-xl text-sm font-semibold"
                  placeholder="votre@email.com"
                />
              </div>
              <button
                type="submit"
                disabled={resetLoading}
                className="orion-login-submit w-full py-4 rounded-xl text-xs font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {resetLoading ? 'Envoi...' : 'Envoyer le code'}
              </button>
              <div className="text-center">
                <button type="button" onClick={() => { setResetMode('login'); setResetMsg(''); }} className="text-[11px] font-bold text-[#546a96] hover:text-[#11c5d6] transition-colors">
                  Retour a la connexion
                </button>
              </div>
            </form>
          )}

          {/* ========== RESET CODE + NEW PASSWORD ========== */}
          {resetMode === 'code' && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              {resetMsg && (
                <div className="p-3.5 rounded-xl border border-[rgba(17,197,214,0.3)] bg-[rgba(17,197,214,0.1)] text-xs font-bold text-[#11c5d6]">
                  {resetMsg}
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-[#7f93bc] uppercase tracking-[0.15em] ml-1">Code recu par email</label>
                <input
                  type="text"
                  required
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  className="orion-login-input w-full px-4 py-3.5 rounded-xl text-sm font-semibold tracking-[0.3em] text-center"
                  placeholder="XXXXXXXX"
                  maxLength={64}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-[#7f93bc] uppercase tracking-[0.15em] ml-1">Nouveau mot de passe</label>
                <input
                  type="password"
                  required
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  className="orion-login-input w-full px-4 py-3.5 rounded-xl text-sm font-semibold"
                  placeholder="••••••••"
                  minLength={3}
                />
              </div>
              <button
                type="submit"
                disabled={resetLoading}
                className="orion-login-submit w-full py-4 rounded-xl text-xs font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {resetLoading ? 'Modification...' : 'Changer le mot de passe'}
              </button>
              <div className="text-center">
                <button type="button" onClick={() => { setResetMode('login'); setResetMsg(''); }} className="text-[11px] font-bold text-[#546a96] hover:text-[#11c5d6] transition-colors">
                  Retour a la connexion
                </button>
              </div>
            </form>
          )}

          {/* Demo accounts */}
          {resetMode === 'login' && (
            <div className="mt-7 pt-6 border-t border-[rgba(100,140,210,0.2)]">
              <p className="text-[10px] font-bold text-[#546a96] uppercase text-center mb-3.5 tracking-[0.15em]">Comptes de test</p>
              <div className="flex justify-center gap-2 flex-wrap">
                {MOCK_USERS.slice(0, 3).map(u => (
                  <button
                    key={u.id}
                    onClick={() => quickFill(u)}
                    className="orion-login-demo-btn px-3.5 py-2 rounded-lg text-[10px] font-bold transition-all duration-200"
                    title={u.role}
                  >
                    {u.name.split(' ')[0]} ({u.role === 'SUPER_ADMIN' ? 'Admin' : u.role === 'COMPANY_ADMIN' ? 'Manager' : 'User'})
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pb-6 text-center">
          <span className="text-[10px] text-[#3d5280] font-medium">ORION CRM v2.0</span>
        </div>
      </div>
    </div>
  );
};

export default Login;
